import React, { useEffect, useState, useCallback } from "react";
import HelloSign from "hellosign-embedded";
import { useDispatch, useSelector } from "react-redux";
import { initializeClient } from "state/slices/esignature";
import SpinnerIcon from "components/lib/Shared/Icons/SpinnerIcon";
import useFullscreenToggle from "hooks/useFullscreenToggle";
import { Constants } from "config/constants";
import esignatureService from "services/esignature";
import { useToast } from "hooks/useToast";

const UnclaimedDraftEmbeddedSigning = ({
  signers,
  fileUrls,
  metadata = null,
  functionsToCall = [],
}) => {
  // Redux state and hooks
  const esignature = useSelector((state) => state.esignatureSlice);
  const { company } = useSelector((state) => state.companySlice);
  const currentUser = useSelector((state) => state.userSlice);
  const dispatch = useDispatch();
  const { toast } = useToast(5000);
  const { isFullscreen, ToggleFullscreenButton } = useFullscreenToggle();

  // Local state for HelloSign response data
  const [responseData, setResponseData] = useState({
    claimUrl: null,
    signatureRequestId: null,
  });
  const { claimUrl, signatureRequestId } = responseData;
  // Derived variables from redux state
  const associatedCompanyId = esignature.companyId;
  const client = esignature.client;

  // Data for HelloSign claim URL API
  const getClaimUrlData = {
    fileUrls,
    signers,
    requester_email_address: currentUser?.electronicAddress?.emailAddress,
    metadata: {
      companyName: company.companyName1WithSuffix,
      ...metadata,
    },
  };
  // Data for assigning signature IDs after signing
  const assignSignatureIdsData = {
    signers,
    signatureRequestId,
    companyName: company.companyName1WithSuffix,
    metadata,
  };

  // Calls HelloSign API to create a claim URL and signature request ID
  const createClaimUrlAndSignatureRequestId = useCallback(async (data) => {
    if (data.fileUrls.length > 0 && data.signers.length > 0) {
      const response = await esignatureService.createClaimUrl(data);
      return response.data;
    }
    return { claimUrl: null, signatureRequestId: null };
  }, []);

  // Calls HelloSign API to assign signature IDs to signers after signing is finished
  const createSignatureIdsForSigners = useCallback(
    async (data) => {
      const response = await esignatureService.createSignatureIds(data);
      if (response.statusText === "OK") {
        toast("success", "Sent for signatures successfully");
      }
    },
    [toast]
  );

  // Opens the HelloSign embedded client in the container
  const openClient = useCallback(() => {
    if (client && claimUrl) {
      client.open(claimUrl, {
        clientId: Constants.HELLO_SIGN.CLIENT_ID,
        skipDomainVerification: true,
        container: document.getElementById("sign-here"),
      });
    }
  }, [client, claimUrl]);

  // Closes the HelloSign embedded client
  const closeClient = useCallback(() => {
    if (client) client.close();
  }, [client]);

  // Registers a one-time event handler for when signing is finished
  // This will assign signature IDs and call any additional functions passed in
  const handleOnceSend = useCallback(() => {
    if (!client) return;
    client.once("finish", () => {
      createSignatureIdsForSigners(assignSignatureIdsData);
      if (Array.isArray(functionsToCall) && functionsToCall.length > 0) {
        functionsToCall.forEach((func) => func && func());
      }
      closeClient();
    });
  }, [
    client,
    createSignatureIdsForSigners,
    assignSignatureIdsData,
    functionsToCall,
    closeClient,
  ]);

  // Initialize HelloSign client if not already initialized
  useEffect(() => {
    if (!esignature.client) {
      const clientInstance = new HelloSign();
      dispatch(initializeClient(clientInstance));
    }
  }, [esignature.client, dispatch]);

  // Fetch claim URL and signature request ID if company context changes
  useEffect(() => {
    const fetchClaimUrl = async () => {
      if (associatedCompanyId !== company._id) {
        const data = await createClaimUrlAndSignatureRequestId(getClaimUrlData);
        setResponseData(data);
      }
    };
    fetchClaimUrl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When claimUrl is available, open HelloSign client and register finish handler
  useEffect(() => {
    if (claimUrl) {
      closeClient();
      openClient();
      handleOnceSend();
    }
  }, [claimUrl, closeClient, openClient, handleOnceSend]);

  if (claimUrl) {
    return (
      <div
        className={
          isFullscreen
            ? "fixed inset-0 bg-white px-4 top-[7.5rem] z-50"
            : "w-full"
        }
      >
        <ToggleFullscreenButton />
        <div id="sign-here" style={{ height: "900px" }}></div>
      </div>
    );
  }

  // Loading state while waiting for claimUrl
  return (
    <div className="flex items-center justify-center h-auto bg-gray-100 py-6">
      <div className="p-6 bg-white rounded shadow-md">
        <h1 className="mb-4 max-w-3xl">
          Please wait a moment while we retrieve the documents for you. Thank
          you for your patience.
        </h1>
        <p></p>
        <p>
          If you have not uploaded the business documents kindly go back to the
          previous step and upload them.
        </p>
        <p className="flex justify-center py-4">
          <SpinnerIcon className="text-gray-500" />
        </p>
      </div>
    </div>
  );
};

export default UnclaimedDraftEmbeddedSigning;
